import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import AssignStaffModal from "../../components/ui/AssignStaffModal";
import DataTable from "../../components/ui/DataTable";
import {
  listLicences,
  getLicenceStats,
  updateLicenceStatus,
  archiveLicence,
} from "../../services/licences";
import {
  listTranslationRequests,
  updateTranslationRequest,
} from "../../services/translationRequests";
import { normalizeTranslationType } from "../../constants/translationRequestType";

/** Tabs that can appear in ?tab= for this page (licence list + translation views). */
const KNOWN_TAB_VALUES = [
  "All",
  "Pending",
  "Self Reviewed",
  "Licence Granted",
  "Expired",
  "Archived",
  "Translation Requests",
  "Translation Requests - Pending",
  "Translation Requests - Approved",
  "Translation Requests - Granted",
];

const RequestedLicences = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(() => {
    const tabFromUrl = searchParams.get("tab");
    if (tabFromUrl && KNOWN_TAB_VALUES.includes(tabFromUrl)) return tabFromUrl;
    return "All";
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [isAssignStaffModalOpen, setIsAssignStaffModalOpen] = useState(false);
  const [selectedLicenceId, setSelectedLicenceId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [licences, setLicences] = useState([]);
  const [translationRequests, setTranslationRequests] = useState([]);
  const [stats, setStats] = useState({
    totalRequests: 0,
    approved: 0,
    selfReviewed: 0,
    archived: 0,
    pending: 0,
    licenceGranted: 0,
    expired: 0,
  });
  const [translationStats, setTranslationStats] = useState({
    total: 0,
    pending: 0,
    underReview: 0,
    approved: 0,
    granted: 0,
  });
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 10,
  });
  const [translationPagination, setTranslationPagination] = useState({
    total: 0,
    page: 1,
    limit: 10,
  });

  const [filterMenuOpen, setFilterMenuOpen] = useState(false);
  const [licenceIdsWithTranslationRequests, setLicenceIdsWithTranslationRequests] = useState(new Set());
  /** YYYY-MM-DD for `submittedAt` range (admin list API) */
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const filterToolbarRef = useRef(null);

  const isTranslationTab =
    activeTab === "Translation Requests" ||
    activeTab === "Translation Requests - Pending" ||
    activeTab === "Translation Requests - Approved" ||
    activeTab === "Translation Requests - Granted";

  // Map status for filtering
  const getStatusFilter = (tab) => {
    if (tab === "All") return "";
    if (tab === "Pending") return "Awaiting Review";
    if (tab === "Self Reviewed") return "Under Review";
    if (tab === "Licence Granted") return "Approved";
    if (tab === "Expired") return "Expired";
    if (tab === "Archived") return "Archived";
    return "";
  };

  // Map status for translation requests filtering
  const getTranslationStatusFilter = (tab) => {
    if (tab === "All") return "";
    if (tab === "Pending") return "Pending";
    if (tab === "Under Review") return "Under Review";
    if (tab === "Approved") return "Approved";
    if (tab === "Granted") return "Granted";
    return "";
  };

  // Load licences
  const loadLicences = async () => {
    try {
      setLoading(true);
      setError(null);
      const statusFilter = getStatusFilter(activeTab);
      const params = {
        status: statusFilter,
        search: searchTerm,
        page: currentPage,
        limit: 10,
      };
      let subFrom = (dateFrom || "").trim();
      let subTo = (dateTo || "").trim();
      if (subFrom && subTo && subFrom > subTo) {
        [subFrom, subTo] = [subTo, subFrom];
      }
      if (subFrom) params.submittedFrom = subFrom;
      if (subTo) params.submittedTo = subTo;

      const response = await listLicences(params);

      setLicences(response.data || []);
      setPagination(response.pagination || { total: 0, page: 1, limit: 10 });

      if (response.stats) {
        setStats(response.stats);
      }
    } catch (err) {
      console.error("Failed to load licences:", err);
      setError("Failed to load licence requests");
      setLicences([]);
    } finally {
      setLoading(false);
    }
  };

  // Load stats
  const loadStats = async () => {
    try {
      const response = await getLicenceStats();
      if (response.data) {
        setStats(response.data);
      }
    } catch (err) {
      console.error("Failed to load stats:", err);
    }
  };

  // Load translation stats
  const loadTranslationStats = async () => {
    try {
      const response = await listTranslationRequests({
        page: 1,
        limit: 1, // Just to get stats, we don't need the data
      });

      if (response && response.success !== false) {
        const data = response.data || response;
        if (data.stats) {
          setTranslationStats(data.stats);
        } else if (data.pagination) {
          // If stats not available, use pagination total as fallback
          setTranslationStats({
            total: data.pagination.total || 0,
            pending: 0,
            underReview: 0,
            approved: 0,
            granted: 0,
          });
        }
      }
    } catch (err) {
      console.error("Failed to load translation stats:", err);
    }
  };

  // Build a set of licence IDs that have associated translation requests
  const loadTranslationRequestLicenceIds = async () => {
    try {
      const ids = new Set();
      let page = 1;
      const limit = 500;
      let hasMore = true;

      while (hasMore) {
        const response = await listTranslationRequests({ page, limit });
        if (!response || response.success === false) break;

        const data = response.data || response;
        const requests = data.translationRequests || [];
        requests.forEach((tr) => {
          // Flag licences whose translation request needs attention (Pending or Under Review)
          const trStatus = (tr.status || tr.translationStatus || '').toLowerCase();
          const attentionStatuses = ['pending', 'under review'];
          if (!attentionStatuses.includes(trStatus)) return;
          const licenceId =
            typeof tr.licenceId === 'string'
              ? tr.licenceId
              : tr.licenceId?._id;
          if (licenceId) ids.add(licenceId);
        });

        const total = data.pagination?.total || 0;
        hasMore = page * limit < total;
        page++;
      }

      setLicenceIdsWithTranslationRequests(ids);
    } catch (err) {
      console.error('Failed to load translation request licence ids:', err);
    }
  };

  useEffect(() => {
    loadStats();
    loadTranslationStats(); // Load translation stats automatically
    loadTranslationRequestLicenceIds(); // Build licence-ids-with-translation-requests set
  }, []);

  // Keep ?tab= in sync when the active tab changes (single source of truth from state)
  useEffect(() => {
    if (activeTab) {
      setSearchParams({ tab: activeTab }, { replace: true });
    }
  }, [activeTab, setSearchParams]);

  // Browser back/forward or external URL change → update tab
  useEffect(() => {
    const t = searchParams.get("tab");
    if (t == null || t === "") return;
    if (!KNOWN_TAB_VALUES.includes(t)) return;
    setActiveTab((prev) => (prev === t ? prev : t));
  }, [searchParams]);

  useEffect(() => {
    if (!filterMenuOpen) return;
    const onDocMouseDown = (e) => {
      if (
        filterToolbarRef.current &&
        !filterToolbarRef.current.contains(e.target)
      ) {
        setFilterMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, [filterMenuOpen]);

  // Load translation requests
  const loadTranslationRequests = async () => {
    try {
      setLoading(true);
      setError(null);
      // For translation requests tab, don't filter by status unless it's a specific status tab
      const statusFilter =
        activeTab === "Translation Requests"
          ? ""
          : getTranslationStatusFilter(activeTab);
      const response = await listTranslationRequests({
        status: statusFilter || undefined,
        search: searchTerm,
        page: currentPage,
        limit: 10,
      });

      if (response && response.success !== false) {
        const data = response.data || response;
        setTranslationRequests(data.translationRequests || []);
        setTranslationPagination(
          data.pagination || { total: 0, page: 1, limit: 10 },
        );

        // Update translation stats from response
        if (data.stats) {
          setTranslationStats(data.stats);
        } else if (data.pagination) {
          // If stats not available, use pagination total as fallback
          setTranslationStats((prev) => ({
            ...prev,
            total: data.pagination.total || 0,
          }));
        }
      }
    } catch (err) {
      console.error("Failed to load translation requests:", err);
      setError("Failed to load translation requests");
      setTranslationRequests([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (
      activeTab === "Translation Requests" ||
      activeTab === "Translation Requests - Pending" ||
      activeTab === "Translation Requests - Approved" ||
      activeTab === "Translation Requests - Granted"
    ) {
      loadTranslationRequests();
    } else {
      loadLicences();
      loadTranslationRequestLicenceIds(); // refresh translation request indicators
    }
  }, [activeTab, currentPage, searchTerm, dateFrom, dateTo]);

  // Reload translation stats when translation requests are loaded
  useEffect(() => {
    if (activeTab === "Translation Requests") {
      loadTranslationStats();
    }
  }, [activeTab]);

  const statsCards = [
    {
      label: "Total Requests",
      count: stats.totalRequests || 0,
      iconType: "document",
      iconColor: "#73827c",
    },
    {
      label: "Approved",
      count: stats.approved || 0,
      iconType: "document",
      iconColor: "#73827c",
    },
    {
      label: "Self Reviewed",
      count: stats.selfReviewed || 0,
      iconType: "document",
      iconColor: "#73827c",
    },
    {
      label: "Archived",
      count: stats.archived || 0,
      iconType: "document",
      iconColor: "#73827c",
    },
  ];

  const tabs = [
    { name: "All", count: stats.totalRequests || 0 },
    { name: "Pending", count: stats.pending || 0 },
    { name: "Self Reviewed", count: stats.selfReviewed || 0 },
    { name: "Licence Granted", count: stats.licenceGranted || 0 },
    { name: "Expired", count: stats.expired || 0 },
    { name: "Archived", count: stats.archived || 0 },
  ];

  // Transform API data to table format
  const transformedLicences = licences.map((licence) => {
    const submittedDate = licence.submittedAt
      ? new Date(licence.submittedAt).toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        })
      : "";

    const amount = licence.fees?.totalPrice
      ? `£${licence.fees.totalPrice.toFixed(2)}`
      : "£0.00";

    const status = licence.status || "Awaiting Review";
    const quoteStatus =
      licence.payment?.status === "Completed"
        ? "Granted"
        : licence.payment?.status === "Pending"
          ? "Pending"
          : licence.payment?.status || "Pending";

    return {
      id: licence._id,
      status: status === "Approved" ? "Granted" : status,
      hasUnreadActivity: licence.hasUnreadActivity || false,
      date: submittedDate,
      organization:
        licence.org?.name || licence.customerId?.organization || "N/A",
      licenceName: licence.coaId?.name || "N/A",
      name: licence.contact?.fullName || licence.customerId?.firstName || "N/A",
      product: "Licence",
      amount,
      quote: quoteStatus,
      reviewer: licence.reviewerId?.fullName || "N/A",
      approver: licence.approverId?.fullName || "N/A",
      refNumber: licence.refNumber || "N/A",
      type: "licence",
      customerActionKey: licence.customerAction?.key || "",
    };
  });

  // Transform translation requests to table format
  const transformedTranslationRequests = translationRequests.map((request) => {
    const submittedDate = request.submittedAt
      ? new Date(request.submittedAt).toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        })
      : "";

    const amount = request.fees?.totalPrice
      ? `£${request.fees.totalPrice.toFixed(2)}`
      : "£0.00";

    // Use translation type for display, fallback to request status
    const displayStatus = request.translationStatus
      ? normalizeTranslationType(request.translationStatus)
      : (request.status || "Pending");

    return {
      id: request._id,
      status: displayStatus,
      translationStatus: request.translationStatus,
      paymentStatus: request.paymentStatus,
      date: submittedDate,
      organization:
        request.customerId?.organization || request.customerId?.name || "N/A",
      licenceName:
        request.coaId?.name || request.licenceId?.coaId?.name || "N/A",
      name: request.customerId?.name || request.customerId?.firstName || "N/A",
      product: "Translation Request",
      amount,
      quote:
        displayStatus === "Granted"
          ? "Granted"
          : displayStatus === "Available"
            ? "Available"
            : displayStatus,
      reviewer: request.reviewerId?.name || "N/A",
      approver: request.approverId?.name || "N/A",
      refNumber: request.licenceId?.refNumber || request.refNumber || "N/A",
      type: "translation-request",
      translationRequest: request,
      customerActionKey: request.licenceId?.customerAction?.key ?? "",
    };
  });

  // Define table columns
  const columns = [
    { header: "Ref#", accessor: "refNumber", nowrap: true },
    { header: "Status", accessor: "status", nowrap: true },
    { header: "Customer Action", accessor: "customerActionKey", nowrap: true },
    { header: "Date", accessor: "date", nowrap: true },
    { header: "Organisation", accessor: "organization" },
    { header: "Licence Name", accessor: "licenceName" },
    { header: "Name", accessor: "name", nowrap: true },
    { header: "Product", accessor: "product", nowrap: true },
    { header: "Amount", accessor: "amount", nowrap: true },
    { header: "Quote", accessor: "quote", nowrap: true },
    { header: "Reviewer", accessor: "reviewer", nowrap: true },
    { header: "Approver", accessor: "approver", nowrap: true },
  ];

  // Define action menu items
  const actionMenuItems = [
    {
      action: "assign-staff",
      label: "Assign Staff",
      icon: (
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
          <path
            d="M10 10C12.3012 10 14.1667 8.13452 14.1667 5.83333C14.1667 3.53215 12.3012 1.66667 10 1.66667C7.69881 1.66667 5.83333 3.53215 5.83333 5.83333C5.83333 8.13452 7.69881 10 10 10Z"
            stroke="#73827c"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M17.1583 18.3333C17.1583 15.1083 13.95 12.5 10 12.5C6.05 12.5 2.84167 15.1083 2.84167 18.3333"
            stroke="#73827c"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ),
    },
    {
      action: "view-detail",
      label: "View Licence Details",
      icon: (
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
          <path
            d="M12.9833 10C12.9833 11.65 11.65 12.9833 10 12.9833C8.35 12.9833 7.01666 11.65 7.01666 10C7.01666 8.35 8.35 7.01666 10 7.01666C11.65 7.01666 12.9833 8.35 12.9833 10Z"
            stroke="#73827c"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M9.99999 16.8917C12.9417 16.8917 15.6833 15.1583 17.5917 12.1583C18.3417 10.9833 18.3417 9.00833 17.5917 7.83333C15.6833 4.83333 12.9417 3.1 9.99999 3.1C7.05832 3.1 4.31666 4.83333 2.40832 7.83333C1.65832 9.00833 1.65832 10.9833 2.40832 12.1583C4.31666 15.1583 7.05832 16.8917 9.99999 16.8917Z"
            stroke="#73827c"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ),
    },
    {
      action: "edit-license-request",
      label: "Edit Licence Request",
      icon: (
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
          <path
            d="M11.05 3.00002L4.20835 10.2417C3.95002 10.5167 3.70002 11.0584 3.65002 11.4334L3.34169 14.1334C3.23335 15.1084 3.93335 15.775 4.90002 15.6084L7.58335 15.175C7.95835 15.1084 8.48335 14.8084 8.74169 14.525L15.5834 7.28335C16.7667 6.03335 17.3 4.60835 15.4584 2.86668C13.625 1.14168 12.2334 1.75002 11.05 3.00002Z"
            stroke="#73827c"
            strokeWidth="1.5"
            strokeMiterlimit="10"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M9.90833 4.20831C10.2667 6.50831 12.1333 8.26665 14.45 8.49998"
            stroke="#73827c"
            strokeWidth="1.5"
            strokeMiterlimit="10"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M2.5 18.3333H17.5"
            stroke="#73827c"
            strokeWidth="1.5"
            strokeMiterlimit="10"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ),
    },
    {
      action: "set-approved",
      label: "Set Approved",
      highlighted: true,
      icon: (
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
          <path
            d="M10 18.3333C14.6024 18.3333 18.3333 14.6024 18.3333 10C18.3333 5.39763 14.6024 1.66667 10 1.66667C5.39763 1.66667 1.66667 5.39763 1.66667 10C1.66667 14.6024 5.39763 18.3333 10 18.3333Z"
            stroke="#222f2a"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M6.45833 10L8.81667 12.3583L13.5417 7.64167"
            stroke="#222f2a"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ),
    },
    {
      action: "archived",
      label: "Archived",
      icon: (
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
          <path
            d="M17.5 6.66667V15C17.5 15.442 17.3244 15.866 17.0118 16.1785C16.6993 16.4911 16.2754 16.6667 15.8333 16.6667H4.16667C3.72464 16.6667 3.30072 16.4911 2.98816 16.1785C2.67559 15.866 2.5 15.442 2.5 15V6.66667"
            stroke="#73827c"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M18.3333 3.33333H1.66667V6.66667H18.3333V3.33333Z"
            stroke="#73827c"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M8.33333 10H11.6667"
            stroke="#73827c"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ),
    },
  ];

  const getStatusClass = (status) => {
    switch (status) {
      case "Granted":
      case "Approved":
        return "bg-emerald-50 text-emerald-600";
      case "Pending":
      case "In Development":
        return "bg-yellow-50 text-yellow-600";
      case "Rejected":
        return "bg-red-50 text-red-600";
      case "Under Review":
        return "bg-blue-50 text-blue-600";
      default:
        return "bg-gray-50 text-gray-600";
    }
  };

  const getQuoteClass = (quote) => {
    switch (quote) {
      case "Granted":
        return "bg-emerald-50 text-emerald-600";
      case "Pending":
        return "bg-yellow-50 text-yellow-600";
      default:
        return "bg-gray-50 text-gray-600";
    }
  };

  // Status badge with red dot when needs attention (same as My Review Queue / My Approval Queue)
  const getStatusBadgeWithAttention = (status, hasUnreadActivity, licenceId) => {
    const baseAttentionStatuses = [
      "Awaiting Review",
      "Under Review",
      "Re-review",
      "Under Review – Additional Info Required",
    ];
    const hasTranslationRequest = licenceId && licenceIdsWithTranslationRequests.has(licenceId);
    const needsAttention =
      baseAttentionStatuses.includes(status) || hasUnreadActivity || hasTranslationRequest;
    return (
      <div className="flex items-center gap-2">
        {needsAttention && (
          <div className="w-2 h-2 bg-red-500 rounded-full flex-shrink-0" />
        )}
        <span
          className={`px-2.5 py-1 inline-flex items-center gap-1 text-xs font-medium rounded-full ${getStatusClass(status)}`}
        >
          <span className="w-1.5 h-1.5 rounded-full bg-current" />
          {status}
        </span>
      </div>
    );
  };

  // Custom cell renderer
  const renderCell = (accessor, row) => {
    switch (accessor) {
      case "customerActionKey":
        const key = row.customerActionKey || (row.type === "translation-request" ? (row.translationRequest?.licenceId?.customerAction?.key ?? "") : "");
        if (!key) return <span className="text-sm text-gray-400">—</span>;
        return (
          <span className="px-2.5 py-1 inline-flex text-xs font-medium rounded-full  bg-red-500 text-[#060202]">
            {key}
          </span>
        );
      case "status":
        return row.type === "licence"
          ? getStatusBadgeWithAttention(row.status, row.hasUnreadActivity, row.id)
          : (
              <span
                className={`px-2.5 py-1 inline-flex items-center gap-1 text-xs font-medium rounded-full ${getStatusClass(row.status)}`}
              >
                <span className="w-1.5 h-1.5 rounded-full bg-current" />
                {row.status}
              </span>
            );
      case "date":
        return <span className="text-sm text-gray-600">{row.date}</span>;
      case "organization":
        return (
          <span className="text-sm text-gray-900">{row.organization}</span>
        );
      case "licenceName":
        return <span className="text-sm text-gray-900">{row.licenceName}</span>;
      case "name":
        return <span className="text-sm text-gray-900">{row.name}</span>;
      case "product":
        return <span className="text-sm text-gray-600">{row.product}</span>;
      case "amount":
        return (
          <span className="text-sm text-gray-900 font-medium">
            {row.amount}
          </span>
        );
      case "quote":
        return (
          <span
            className={`px-2.5 py-1 inline-flex items-center gap-1 text-xs font-medium rounded-full ${getQuoteClass(row.quote)}`}
          >
            <span className="w-1.5 h-1.5 rounded-full bg-current"></span>
            {row.quote}
          </span>
        );
      case "reviewer":
        return <span className="text-sm text-gray-900">{row.reviewer}</span>;
      case "approver":
        return <span className="text-sm text-gray-900">{row.approver}</span>;
      case "refNumber":
        return <span className="text-sm text-gray-600">{row.refNumber}</span>;
      default:
        return <span className="text-sm text-gray-900">{row[accessor]}</span>;
    }
  };

  // Action menu items for translation requests - only View and Reject
  const translationActionMenuItems = [
    {
      action: "view-detail",
      label: "View Translation Request",
      icon: (
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
          <path
            d="M12.9833 10C12.9833 11.65 11.65 12.9833 10 12.9833C8.35 12.9833 7.01666 11.65 7.01666 10C7.01666 8.35 8.35 7.01666 10 7.01666C11.65 7.01666 12.9833 8.35 12.9833 10Z"
            stroke="#73827c"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M9.99999 16.8917C12.9417 16.8917 15.6833 15.1583 17.5917 12.1583C18.3417 10.9833 18.3417 9.00833 17.5917 7.83333C15.6833 4.83333 12.9417 3.1 9.99999 3.1C7.05832 3.1 4.31666 4.83333 2.40832 7.83333C1.65832 9.00833 1.65832 10.9833 2.40832 12.1583C4.31666 15.1583 7.05832 16.8917 9.99999 16.8917Z"
            stroke="#73827c"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ),
    },
    {
      action: "reject",
      label: "Reject",
      icon: (
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
          <path
            d="M10 18.3333C14.6024 18.3333 18.3333 14.6024 18.3333 10C18.3333 5.39763 14.6024 1.66667 10 1.66667C5.39763 1.66667 1.66667 5.39763 1.66667 10C1.66667 14.6024 5.39763 18.3333 10 18.3333Z"
            stroke="#73827c"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M12.5 7.5L7.5 12.5M7.5 7.5L12.5 12.5"
            stroke="#73827c"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ),
    },
  ];

  const handleRowAction = async (action, row) => {
    try {
      // Extract id from row object (row can be either an id string or a row object)
      const id = typeof row === "string" ? row : row?.id || row?._id;

      if (!id) {
        console.error("No ID found in row:", row);
        alert("Error: No licence ID found");
        return;
      }

      if (action === "assign-staff") {
        setSelectedLicenceId(id);
        setIsAssignStaffModalOpen(true);
      } else if (action === "view-detail") {
        navigate(`/licence-details/${id}`);
      } else if (action === "edit-licence") {
        navigate(`/edit-licence/${id}`);
      } else if (action === "edit-license-request") {
        navigate(`/edit-license-request/${id}`);
      } else if (action === "set-approved") {
        await updateLicenceStatus(id, "Approved");
        await loadLicences();
        await loadStats();
      } else if (action === "archived") {
        await archiveLicence(id);
        await loadLicences();
        await loadStats();
      }
    } catch (err) {
      console.error(`Failed to ${action}:`, err);
      const errorMessage = err?.message || err?.data?.error || `Failed to ${action}: Unknown error`;
      alert(errorMessage);
    }
  };

  const handleTranslationRowAction = async (action, row) => {
    try {
      const id = typeof row === "string" ? row : row?.id || row?._id;

      if (!id) {
        console.error("No ID found in row:", row);
        alert("Error: No translation request ID found");
        return;
      }

      if (action === "view-detail") {
        // Navigate to translation request details page
        navigate(`/translation-request/${id}`);
      } else if (action === "reject") {
        const reason = prompt("Please provide a reason for rejection:");
        if (reason) {
          await updateTranslationRequest(id, {
            status: "Rejected",
            rejectionReason: reason,
          });
          await loadTranslationRequests();
        }
      }
    } catch (err) {
      console.error(`Failed to ${action}:`, err);
      alert(`Failed to ${action}: ${err.message || "Unknown error"}`);
    }
  };

  return (
    <>
      <AssignStaffModal
        isOpen={isAssignStaffModalOpen}
        onClose={() => {
          setIsAssignStaffModalOpen(false);
          setSelectedLicenceId(null);
          loadLicences();
        }}
        licenceId={selectedLicenceId}
      />

      <div className="space-y-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {statsCards.map((stat, index) => {
            const getTabForCard = (label) => {
              if (label === "Total Requests") return "All";
              if (label === "Approved") return "Licence Granted";
              if (label === "Self Reviewed") return "Self Reviewed";
              if (label === "Archived") return "Archived";
              return "All";
            };

            const tabToOpen = getTabForCard(stat.label);
            const isSummaryActive =
              (stat.label === "Total Requests" && activeTab === "All") ||
              (stat.label === "Approved" && activeTab === "Licence Granted") ||
              (stat.label === "Self Reviewed" && activeTab === "Self Reviewed") ||
              (stat.label === "Archived" && activeTab === "Archived");

            return (
              <button
                type="button"
                key={index}
                onClick={() => {
                  setActiveTab(tabToOpen);
                  setCurrentPage(1);
                }}
                className={`rounded-xl border p-3 text-left transition-all ${
                  isSummaryActive
                    ? "border-emerald-400 bg-emerald-50 shadow-md ring-1 ring-emerald-200"
                    : "border-gray-200 bg-white shadow-sm hover:border-emerald-200 hover:shadow-md"
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
                      <path
                        d="M9.33333 2H4C3.46957 2 2.96086 2.21071 2.58579 2.58579C2.21071 2.96086 2 3.46957 2 4V12C2 12.5304 2.21071 13.0391 2.58579 13.4142C2.96086 13.7893 3.46957 14 4 14H12C12.5304 14 13.0391 13.7893 13.4142 13.4142C13.7893 13.0391 14 12.5304 14 12V6.66667L9.33333 2Z"
                        stroke={stat.iconColor}
                        strokeWidth="1.2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M9.33333 2V6.66667H14"
                        stroke={stat.iconColor}
                        strokeWidth="1.2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    <p className="text-xs text-gray-500 font-normal">
                      {stat.label}
                    </p>
                  </div>
                  <p className="text-xl font-bold text-gray-900">
                    {stat.count}
                  </p>
                </div>
              </button>
            );
          })}
        </div>

        {/* Page Title and Actions */}
        <div className="flex items-center justify-between">
          <h1 className="text-[28px] font-semibold text-[#0a1f17] font-inter tracking-[-0.56px] leading-[1.4]">
            Requested Licences{" "}
          </h1>

          <div className="relative flex items-center gap-3" ref={filterToolbarRef}>
            <button
              type="button"
              disabled={isTranslationTab}
              title={
                isTranslationTab
                  ? "Date filters apply to licence requests only"
                  : undefined
              }
              aria-expanded={filterMenuOpen}
              aria-haspopup="true"
              onClick={() => {
                if (isTranslationTab) return;
                setFilterMenuOpen((o) => !o);
              }}
              className={`flex items-center gap-2 px-4 py-2 text-sm rounded-lg border transition-colors ${
                isTranslationTab
                  ? "cursor-not-allowed border-gray-200 text-gray-400 bg-gray-50"
                  : dateFrom || dateTo
                    ? "border-emerald-400 bg-emerald-50 text-emerald-800 hover:bg-emerald-100"
                    : "border-gray-300 text-gray-600 hover:bg-gray-50"
              }`}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path
                  d="M2 5.33333H14"
                  stroke="currentColor"
                  strokeWidth="1.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M2 10.6667H14"
                  stroke="currentColor"
                  strokeWidth="1.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M4.66667 2V14"
                  stroke="currentColor"
                  strokeWidth="1.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              Filter
              {(dateFrom || dateTo) && !isTranslationTab ? (
                <span className="ml-1 rounded-full bg-emerald-500 px-1.5 py-0.5 text-[10px] font-semibold text-white">
                  On
                </span>
              ) : null}
            </button>

            {filterMenuOpen && !isTranslationTab && (
              <div
                className="absolute right-0 top-[calc(100%+8px)] z-50 w-[min(100vw-2rem,22rem)] rounded-xl border border-gray-200 bg-white py-3 shadow-lg"
                role="dialog"
                aria-label="Submitted date range"
              >
                <p className="border-b border-gray-100 px-4 pb-2 text-xs font-medium text-gray-500">
                  Filter by request submitted date
                </p>
                <div className="flex flex-col gap-3 px-4 pt-3">
                  <label className="block text-sm text-gray-700">
                    <span className="mb-1 block text-xs font-medium text-gray-500">
                      From
                    </span>
                    <input
                      type="date"
                      value={dateFrom}
                      onChange={(e) => {
                        setDateFrom(e.target.value);
                        setCurrentPage(1);
                      }}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                    />
                  </label>
                  <label className="block text-sm text-gray-700">
                    <span className="mb-1 block text-xs font-medium text-gray-500">
                      To
                    </span>
                    <input
                      type="date"
                      value={dateTo}
                      onChange={(e) => {
                        setDateTo(e.target.value);
                        setCurrentPage(1);
                      }}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                    />
                  </label>
                  {dateFrom && dateTo && dateFrom > dateTo ? (
                    <p className="text-xs text-amber-700">
                      From is after To — range will be applied swapped so results
                      still show the selected window.
                    </p>
                  ) : null}
                </div>
                <div className="mt-3 flex justify-end gap-2 border-t border-gray-100 px-3 pt-3">
                  <button
                    type="button"
                    className="rounded-lg px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100"
                    onClick={() => {
                      setDateFrom("");
                      setDateTo("");
                      setCurrentPage(1);
                      setFilterMenuOpen(false);
                    }}
                  >
                    Clear
                  </button>
                  <button
                    type="button"
                    className="rounded-lg bg-emerald-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-emerald-700"
                    onClick={() => setFilterMenuOpen(false)}
                  >
                    Done
                  </button>
                </div>
              </div>
            )}
            <div className="relative">
              <input
                type="text"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-64 pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-transparent"
              />
              <svg
                className="absolute left-3 top-2.5 w-4 h-4 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>
          </div>
        </div>

        {/* Main Content Card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          {/* Tabs Section */}
          <div className="px-6 py-4">
            <div className="flex items-center gap-2 w-full">
              {tabs.map((tab) => (
                <button
                  type="button"
                  key={tab.name}
                  onClick={() => {
                    setActiveTab(tab.name);
                    setCurrentPage(1);
                  }}
                  className={`flex-1 px-6 py-3 text-sm font-medium transition-all rounded-lg ${
                    activeTab === tab.name
                      ? "bg-emerald-50 text-emerald-600"
                      : "text-gray-600 hover:bg-gray-50"
                  }`}
                >
                  {tab.name}
                  <span
                    className={`ml-2 px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                      activeTab === tab.name
                        ? "bg-emerald-500 text-white"
                        : "bg-gray-200 text-gray-600"
                    }`}
                  >
                    {tab.count}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Data Table */}
          {loading ? (
            <div className="p-8 text-center text-gray-500">Loading...</div>
          ) : error ? (
            <div className="p-8 text-center text-red-500">{error}</div>
          ) : (
            <DataTable
              columns={columns}
              data={transformedLicences}
              currentPage={currentPage}
              onPageChange={setCurrentPage}
              rowsPerPage={10}
              pagination={pagination}
              showPagination={true}
              actionMenuItems={actionMenuItems}
              onRowAction={handleRowAction}
              renderCell={renderCell}
            />
          )}
        </div>
      </div>
    </>
  );
};

export default RequestedLicences;
